import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Star, Search, Filter, Download, Eye, CheckCircle, XCircle,
    MessageSquare, ThumbsUp, Calendar, User, Package, Loader2,
    ChevronLeft, ChevronRight, Shield, AlertCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { adminApi } from '@/services/api/admin';
import { formatDate } from '@/utils/formatters';

interface Review {
    id: number;
    product_id: number;
    user_id: number;
    order_item_id: number;
    rating: number;
    title: string;
    comment: string;
    is_verified_purchase: number;
    is_approved: number;
    helpful_count: number;
    created_at: string;
    updated_at: string;
    product_name: string;
    user_email: string;
}

interface Filters {
    rating?: string;
    status?: string;
    verified?: string;
    search?: string;
    page?: number;
    per_page?: number;
}

interface AdminReviewsManagementProps {
    onNavigateBack?: () => void;
}

const AdminReviewsManagementComponent: React.FC<AdminReviewsManagementProps> = ({
    onNavigateBack
}) => {
    const [reviews, setReviews] = useState<Review[]>([]);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0
    });
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [ratingFilter, setRatingFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [verifiedFilter, setVerifiedFilter] = useState<string>('all');
    const [selectedReview, setSelectedReview] = useState<Review | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [processingIds, setProcessingIds] = useState<Set<number>>(new Set());

    const searchTimeoutRef = useRef<NodeJS.Timeout>();
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Función para cargar reseñas con debounce
    const fetchReviews = useCallback(async (filters: Filters = {}) => {
        setIsLoading(true);
        try {
            const response = await adminApi.getReviews({
                rating: filters.rating !== 'all' ? Number(filters.rating) : undefined,
                status: filters.status !== 'all' ? filters.status : undefined,
                //verified: filters.verified !== 'all' ? filters.verified : undefined,
                search: filters.search,
                page: filters.page || 1,
                per_page: filters.per_page || 10
            });

            setReviews(response.data);
            setPagination({
                current_page: response.meta.current_page,
                per_page: response.meta.per_page,
                total: response.meta.total,
                last_page: response.meta.last_page,
                from: response.meta.from,
                to: response.meta.to
            });

        } catch (error: any) {
            console.error('Error loading reviews:', error);
            toast.error('Error al cargar las reseñas');
            setReviews([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Cargar reseñas inicialmente y cuando cambien los filtros principales
    useEffect(() => {
        const filters: Filters = {
            rating: ratingFilter,
            status: statusFilter,
            verified: verifiedFilter,
            page: 1
        };

        fetchReviews(filters);
    }, [ratingFilter, statusFilter, verifiedFilter, fetchReviews]);

    // Debounce para la búsqueda
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            if (searchTerm.trim() !== '') {
                fetchReviews({
                    search: searchTerm,
                    rating: ratingFilter,
                    status: statusFilter,
                    verified: verifiedFilter,
                    page: 1
                });
            } else if (searchTerm === '') {
                fetchReviews({
                    rating: ratingFilter,
                    status: statusFilter,
                    verified: verifiedFilter,
                    page: 1
                });
            }
        }, 500);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm, ratingFilter, statusFilter, verifiedFilter, fetchReviews]);

    const handleApproveReview = async (reviewId: number) => {
        setProcessingIds(prev => new Set(prev).add(reviewId));

        /*try {
            await adminApi.approveReview(reviewId);

            setReviews(prev => prev.map(review =>
                review.id === reviewId
                    ? { ...review, is_approved: 1, updated_at: new Date().toISOString() }
                    : review
            ));

            toast.success('Reseña aprobada exitosamente');
        } catch (error: any) {
            console.error('Error approving review:', error);
            toast.error('Error al aprobar la reseña');
        } finally {
            setProcessingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(reviewId);
                return newSet;
            });
        }*/
    };

    const handleRejectReview = async (reviewId: number) => {
        setProcessingIds(prev => new Set(prev).add(reviewId));

        try {
            /*await adminApi.rejectReview(reviewId);

            setReviews(prev => prev.map(review =>
                review.id === reviewId
                    ? { ...review, is_approved: 0, updated_at: new Date().toISOString() }
                    : review
            ));

            toast.success('Reseña rechazada');*/
        } catch (error: any) {
            console.error('Error rejecting review:', error);
            toast.error('Error al rechazar la reseña');
        } finally {
            setProcessingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(reviewId);
                return newSet;
            });
        }
    };

    const handlePageChange = (page: number) => {
        fetchReviews({
            rating: ratingFilter !== 'all' ? ratingFilter : undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            verified: verifiedFilter !== 'all' ? verifiedFilter : undefined,
            search: searchTerm || undefined,
            page: page,
            per_page: pagination.per_page
        });

        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePerPageChange = (perPage: number) => {
        fetchReviews({
            rating: ratingFilter !== 'all' ? ratingFilter : undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            verified: verifiedFilter !== 'all' ? verifiedFilter : undefined,
            search: searchTerm || undefined,
            page: 1,
            per_page: perPage
        });
    };

    const openDetailModal = (review: Review) => {
        setSelectedReview(review);
        setShowDetailModal(true);
    };

    // Render stars based on rating
    const renderStars = (rating: number) => {
        return Array.from({ length: 5 }, (_, index) => (
            <Star
                key={index}
                className={`w-4 h-4 ${index < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                    }`}
            />
        ));
    };

    // Componente de paginación
    const PaginationControls = () => (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
            <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">
                    Mostrando {pagination.from} a {pagination.to} de {pagination.total} resultados
                </span>
                <select
                    value={pagination.per_page}
                    onChange={(e) => handlePerPageChange(Number(e.target.value))}
                    className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                    <option value={10}>10 por página</option>
                    <option value={25}>25 por página</option>
                    <option value={50}>50 por página</option>
                    <option value={100}>100 por página</option>
                </select>
            </div>

            <div className="flex items-center space-x-2">
                <button
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={pagination.current_page === 1}
                    className="p-1 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>

                {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                    const page = pagination.current_page <= 3
                        ? i + 1
                        : Math.min(pagination.current_page + i - 2, pagination.last_page);

                    return (
                        <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`w-8 h-8 rounded-md text-sm ${pagination.current_page === page
                                ? 'bg-primary-600 text-white'
                                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                        >
                            {page}
                        </button>
                    );
                })}

                <button
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={pagination.current_page === pagination.last_page}
                    className="p-1 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );

    // Estadísticas
    const stats = {
        total: pagination.total,
        pending: reviews.filter(r => r.is_approved === 0).length,
        approved: reviews.filter(r => r.is_approved === 1).length,
        averageRating: reviews.reduce((sum, review) => sum + review.rating, 0) / (reviews.length || 1),
        verified: reviews.filter(r => r.is_verified_purchase === 1).length
    };

    if (isLoading && reviews.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando reseñas...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                                <MessageSquare className="w-6 h-6 mr-2" />
                                Gestión de Reseñas
                            </h1>
                            <p className="mt-1 text-gray-600">
                                Administra las reseñas de productos de los clientes
                            </p>
                        </div>
                        <div className="mt-4 sm:mt-0 flex space-x-3">
                            <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                                <Download className="w-4 h-4 mr-2" />
                                Exportar
                            </button>
                            {onNavigateBack && (
                                <button
                                    onClick={onNavigateBack}
                                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
                                >
                                    Volver al Dashboard
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <MessageSquare className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="ml-3">
                                <p className="text-xs font-medium text-gray-600">Total</p>
                                <p className="text-lg font-bold text-gray-900">{stats.total}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-yellow-100 rounded-lg">
                                <AlertCircle className="w-5 h-5 text-yellow-600" />
                            </div>
                            <div className="ml-3">
                                <p className="text-xs font-medium text-gray-600">Pendientes</p>
                                <p className="text-lg font-bold text-gray-900">{stats.pending}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                            <div className="ml-3">
                                <p className="text-xs font-medium text-gray-600">Aprobadas</p>
                                <p className="text-lg font-bold text-gray-900">{stats.approved}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <Star className="w-5 h-5 text-purple-600" />
                            </div>
                            <div className="ml-3">
                                <p className="text-xs font-medium text-gray-600">Rating Promedio</p>
                                <p className="text-lg font-bold text-gray-900">{stats.averageRating.toFixed(1)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <Shield className="w-5 h-5 text-green-600" />
                            </div>
                            <div className="ml-3">
                                <p className="text-xs font-medium text-gray-600">Verificadas</p>
                                <p className="text-lg font-bold text-gray-900">{stats.verified}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Buscar por producto, usuario o comentario..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                        </div>

                        {/* Rating Filter */}
                        <div className="relative">
                            <Star className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <select
                                value={ratingFilter}
                                onChange={(e) => setRatingFilter(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none"
                            >
                                <option value="all">Todos los ratings</option>
                                <option value="5">5 Estrellas</option>
                                <option value="4">4 Estrellas</option>
                                <option value="3">3 Estrellas</option>
                                <option value="2">2 Estrellas</option>
                                <option value="1">1 Estrella</option>
                            </select>
                        </div>

                        {/* Status Filter */}
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none"
                            >
                                <option value="all">Todos los estados</option>
                                <option value="approved">Aprobadas</option>
                                <option value="pending">Pendientes</option>
                            </select>
                        </div>

                        {/* Verified Filter */}
                        <div className="relative">
                            <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <select
                                value={verifiedFilter}
                                onChange={(e) => setVerifiedFilter(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none"
                            >
                                <option value="all">Todas las compras</option>
                                <option value="verified">Verificadas</option>
                                <option value="unverified">No verificadas</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Reviews Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Producto & Usuario
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Rating
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Reseña
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Estado
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Fecha
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {reviews.map((review) => (
                                    <tr key={review.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                                                    <Package className="w-5 h-5 text-primary-600" />
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {review.product_name}
                                                    </div>
                                                    <div className="text-sm text-gray-500 flex items-center">
                                                        <User className="w-3 h-3 mr-1" />
                                                        {review.user_email}
                                                    </div>
                                                    {review.is_verified_purchase === 1 && (
                                                        <div className="text-xs text-green-600 flex items-center">
                                                            <Shield className="w-3 h-3 mr-1" />
                                                            Compra verificada
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center space-x-1">
                                                {renderStars(review.rating)}
                                                <span className="text-sm text-gray-600 ml-1">
                                                    ({review.rating})
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-medium text-gray-900">
                                                {review.title}
                                            </div>
                                            <div className="text-sm text-gray-500 line-clamp-2">
                                                {review.comment}
                                            </div>
                                            <div className="text-xs text-gray-400 flex items-center mt-1">
                                                <ThumbsUp className="w-3 h-3 mr-1" />
                                                {review.helpful_count} útiles
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${review.is_approved === 1
                                                ? 'bg-green-100 text-green-800 border border-green-200'
                                                : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                                }`}>
                                                {review.is_approved === 1 ? (
                                                    <>
                                                        <CheckCircle className="w-3 h-3 mr-1" />
                                                        Aprobada
                                                    </>
                                                ) : (
                                                    <>
                                                        <AlertCircle className="w-3 h-3 mr-1" />
                                                        Pendiente
                                                    </>
                                                )}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(review.created_at)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => openDetailModal(review)}
                                                    className="text-primary-600 hover:text-primary-700 p-1 rounded hover:bg-primary-50"
                                                    title="Ver detalles"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>

                                                {review.is_approved === 0 && (
                                                    <button
                                                        onClick={() => handleApproveReview(review.id)}
                                                        disabled={processingIds.has(review.id)}
                                                        className="text-green-600 hover:text-green-700 p-1 rounded hover:bg-green-50 disabled:opacity-50"
                                                        title="Aprobar reseña"
                                                    >
                                                        {processingIds.has(review.id) ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <CheckCircle className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                )}

                                                {review.is_approved === 1 && (
                                                    <button
                                                        onClick={() => handleRejectReview(review.id)}
                                                        disabled={processingIds.has(review.id)}
                                                        className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50 disabled:opacity-50"
                                                        title="Rechazar reseña"
                                                    >
                                                        {processingIds.has(review.id) ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <XCircle className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {reviews.length === 0 && !isLoading && (
                        <div className="text-center py-12">
                            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay reseñas</h3>
                            <p className="text-gray-500">
                                {searchTerm || ratingFilter !== 'all' || statusFilter !== 'all' || verifiedFilter !== 'all'
                                    ? 'No se encontraron reseñas que coincidan con los filtros'
                                    : 'No hay reseñas disponibles'}
                            </p>
                        </div>
                    )}

                    {reviews.length > 0 && <PaginationControls />}
                </div>

                {/* Detail Modal */}
                {showDetailModal && selectedReview && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                                    <MessageSquare className="w-5 h-5 mr-2" />
                                    Detalles de la Reseña
                                </h2>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* Información del Producto y Usuario */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                                            <Package className="w-5 h-5 mr-2" />
                                            Información del Producto
                                        </h3>
                                        <div className="space-y-2">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Nombre</label>
                                                <p className="text-sm text-gray-900">{selectedReview.product_name}</p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">ID del Producto</label>
                                                <p className="text-sm text-gray-900">#{selectedReview.product_id}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                                            <User className="w-5 h-5 mr-2" />
                                            Información del Usuario
                                        </h3>
                                        <div className="space-y-2">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Email</label>
                                                <p className="text-sm text-gray-900">{selectedReview.user_email}</p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">ID del Usuario</label>
                                                <p className="text-sm text-gray-900">#{selectedReview.user_id}</p>
                                            </div>
                                            {selectedReview.is_verified_purchase === 1 && (
                                                <div className="flex items-center text-green-600">
                                                    <Shield className="w-4 h-4 mr-1" />
                                                    <span className="text-sm">Compra verificada</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Rating y Contenido */}
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-3">Contenido de la Reseña</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Rating</label>
                                            <div className="flex items-center space-x-1">
                                                {renderStars(selectedReview.rating)}
                                                <span className="text-sm text-gray-600 ml-2">
                                                    ({selectedReview.rating} de 5 estrellas)
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Título</label>
                                            <p className="text-sm text-gray-900 font-medium">{selectedReview.title}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Comentario</label>
                                            <p className="text-sm text-gray-900 bg-gray-50 rounded-lg p-4">
                                                {selectedReview.comment}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Metadatos */}
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                                        <Calendar className="w-5 h-5 mr-2" />
                                        Metadatos
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Estado</label>
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${selectedReview.is_approved === 1
                                                ? 'bg-green-100 text-green-800 border border-green-200'
                                                : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                                }`}>
                                                {selectedReview.is_approved === 1 ? 'Aprobada' : 'Pendiente'}
                                            </span>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Votos útiles</label>
                                            <p className="text-sm text-gray-900">{selectedReview.helpful_count}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Fecha de creación</label>
                                            <p className="text-sm text-gray-900">{formatDate(selectedReview.created_at)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="p-6 border-t border-gray-200 flex justify-between">
                                <button
                                    onClick={() => setShowDetailModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Cerrar
                                </button>

                                <div className="flex space-x-3">
                                    {selectedReview.is_approved === 0 && (
                                        <button
                                            onClick={() => {
                                                handleApproveReview(selectedReview.id);
                                                setShowDetailModal(false);
                                            }}
                                            disabled={processingIds.has(selectedReview.id)}
                                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center"
                                        >
                                            {processingIds.has(selectedReview.id) ? (
                                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                            ) : (
                                                <CheckCircle className="w-4 h-4 mr-2" />
                                            )}
                                            Aprobar
                                        </button>
                                    )}

                                    {selectedReview.is_approved === 1 && (
                                        <button
                                            onClick={() => {
                                                handleRejectReview(selectedReview.id);
                                                setShowDetailModal(false);
                                            }}
                                            disabled={processingIds.has(selectedReview.id)}
                                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center"
                                        >
                                            {processingIds.has(selectedReview.id) ? (
                                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                            ) : (
                                                <XCircle className="w-4 h-4 mr-2" />
                                            )}
                                            Rechazar
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminReviewsManagementComponent;